/**
 * Choosing which photographs to show.
 *
 * The naive version of "the best ones" is a score and a top-N, and it produces
 * a terrible slideshow: twenty pictures of the same dinner, because the dinner
 * was well lit and somebody held the shutter down. What makes a trip watchable
 * is *spread* — a few from each part of it, in order — so the score decides
 * which photograph represents a moment, and the spread decides how many moments
 * get a turn.
 *
 * No DOM here, and no model either. Under end-to-end encryption the server has
 * never seen these pixels and neither has anything else, so the only signals
 * available are the ones a person or a camera left behind: what was favourited,
 * when it was taken, and how big it is. That turns out to be enough.
 */

export interface Candidate {
  id: string;
  /** Milliseconds. Capture time where the camera recorded it. */
  takenAt: number;
  favorited: boolean;
  width: number | null;
  height: number | null;
  kind: "photo" | "video";
}

/**
 * How much this photograph deserves to represent its moment.
 *
 * Deliberately crude and deliberately explainable. Every term is something a
 * person would give the same reason for.
 */
export function score(candidate: Candidate): number {
  let total = 0;

  /*
   * Somebody said they liked it. Nothing else here is a *judgement* — the rest
   * are proxies — so this outweighs all of them together.
   */
  if (candidate.favorited) total += 100;

  const { width, height } = candidate;
  if (width && height) {
    /*
     * Bigger usually means the real camera rather than a screenshot or a
     * forwarded message, but with sharply diminishing returns.
     *
     * The coefficient is chosen so the curve is still climbing across the range
     * people actually have. At ×6 it hit the cap at about nine megapixels,
     * which meant every photograph from every phone made in the last decade
     * scored exactly the same and the term discriminated between nothing. ×3
     * reaches the cap around a hundred, so a screenshot, a phone photograph and
     * a camera raw are still told apart.
     */
    const megapixels = (width * height) / 1_000_000;
    total += Math.min(20, Math.log2(1 + megapixels) * 3);

    /*
     * Shape, against a full screen. A 9:16 phone photograph and a 16:9 frame
     * cannot both fill it, and something has to give — so ordinary proportions
     * are preferred over extremes, and a panorama is not disqualified, merely
     * asked to be better than average to earn its place.
     */
    const aspect = width / height;
    if (aspect >= 0.6 && aspect <= 1.9) total += 10;
    else if (aspect < 0.35 || aspect > 3) total -= 10;
  }

  return total;
}

/**
 * How long a gap makes two photographs different moments.
 *
 * Twenty minutes: long enough that a burst, a panorama attempt and three tries
 * at the same group photo count as one thing, short enough that the walk before
 * dinner and dinner itself do not merge.
 */
export const MOMENT_GAP_MS = 20 * 60 * 1000;

/** Split a chronological run into moments, wherever there is a long enough gap. */
export function groupIntoMoments(candidates: Candidate[]): Candidate[][] {
  const ordered = [...candidates].sort((a, b) => a.takenAt - b.takenAt);
  const moments: Candidate[][] = [];

  for (const candidate of ordered) {
    const current = moments[moments.length - 1];
    const previous = current?.[current.length - 1];

    if (
      current &&
      previous &&
      candidate.takenAt - previous.takenAt <= MOMENT_GAP_MS
    ) {
      current.push(candidate);
    } else {
      moments.push([candidate]);
    }
  }

  return moments;
}

/**
 * Pick a watchable set, in the order it happened.
 *
 * Every moment gets one photograph before any moment gets two, so a short show
 * of a long trip still visits the whole trip. Only once every moment has been
 * represented does the remaining room go to the best of what is left.
 *
 * Videos are left out. They need a different player, a different duration and a
 * decision about sound, and a slideshow that silently skips them is a clearer
 * promise than one that stalls on them.
 */
export function pickHighlights(
  candidates: Candidate[],
  limit: number,
): string[] {
  const photos = candidates.filter((candidate) => candidate.kind === "photo");
  if (photos.length === 0 || limit <= 0) return [];

  const moments = groupIntoMoments(photos).map((moment) =>
    [...moment].sort((a, b) => score(b) - score(a) || a.takenAt - b.takenAt),
  );

  const chosen: Candidate[] = [];
  // Round after round, one per moment, until there is no more room.
  for (let round = 0; chosen.length < limit; round++) {
    const taken = chosen.length;
    for (const moment of moments) {
      if (chosen.length >= limit) break;
      const candidate = moment[round];
      if (candidate) chosen.push(candidate);
    }
    // A whole pass that added nothing means every photograph is already in.
    if (chosen.length === taken) break;
  }

  return chosen
    .sort((a, b) => a.takenAt - b.takenAt)
    .map((candidate) => candidate.id);
}

/** Everything, oldest first — the whole trip, one by one. */
export function everything(candidates: Candidate[]): string[] {
  return candidates
    .filter((candidate) => candidate.kind === "photo")
    .sort((a, b) => a.takenAt - b.takenAt)
    .map((candidate) => candidate.id);
}
